import { useState } from "react";
import { Link } from "react-router-dom";

const quickLinks = [
  { label: "Home", path: "/" },
  { label: "Properties", path: "/properties" },
  { label: "Social Housing", path: "/social-housing" },
  { label: "About Us", path: "/about" },
  { label: "Contact", path: "/contact" },
  { label: "Investment Brochure", path: "/brochure" },
];

const productLinks = [
  { label: "Social Housing Product", path: "/social-housing" },
  { label: "NASDAQ Dubai Bonds", path: "/contact" },
  { label: "Express Interest", path: "/social-housing#apply" },
  { label: "Investment Brochure", path: "/brochure" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    const formData = new URLSearchParams();
    formData.append("email", email);
    try {
      await fetch("https://readdy.ai/api/form/d73vkr6csbv12mkuct20", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setSubmitted(true);
      setEmail("");
    } catch {
      /* silent */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer
      style={{
        backgroundColor: "#071A2E",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* Gold accent bar */}
      <div
        className="h-[2px] w-full"
        style={{ background: "linear-gradient(90deg, transparent 0%, #C9A84C 30%, #E8C56A 50%, #C9A84C 70%, transparent 100%)" }}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12 mb-14">

          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <div
                className="w-10 h-10 flex items-center justify-center rounded-lg border border-[#C9A84C]/40"
                style={{ backgroundColor: "rgba(201,168,76,0.12)", boxShadow: "0 0 16px rgba(201,168,76,0.18)" }}
              >
                <i className="ri-home-4-fill text-[#C9A84C] text-base leading-none" />
              </div>
              <div>
                <span
                  className="font-bold text-sm tracking-[0.1em] uppercase text-white block leading-none"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Thorpe Dellow
                </span>
                <span className="text-[9px] tracking-[0.18em] uppercase text-[#C9A84C]/70 block mt-0.5">
                  Structured Investments
                </span>
              </div>
            </div>
            <p className="text-slate-400 text-xs leading-relaxed mb-6">
              Specialist social housing investment products backed by UK assisted living assets. Targeting 10.00% p.a. for qualified investors.
            </p>

            {submitted ? (
              <p className="text-[#C9A84C] text-xs font-semibold flex items-center gap-1.5">
                <i className="ri-check-line leading-none" /> Subscribed successfully
              </p>
            ) : (
              <form
                id="footer-newsletter"
                onSubmit={handleSubscribe}
                data-readdy-form
                className="flex flex-col gap-2.5"
              >
                <input
                  type="email"
                  name="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-white/6 border border-white/12 rounded-lg px-4 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-[#C9A84C]/50 transition-colors"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide bg-[#C9A84C] text-[#071A2E] hover:bg-[#B8953B] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {submitting ? "Subscribing..." : "Subscribe to Updates"}
                </button>
              </form>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-[#C9A84C] font-bold text-[10px] mb-5 uppercase tracking-[0.18em]">
              Navigation
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((l) => (
                <li key={l.path}>
                  <Link
                    to={l.path}
                    className="text-slate-400 hover:text-[#C9A84C] text-xs transition-colors cursor-pointer flex items-center gap-2 group"
                  >
                    <span className="w-3 h-px bg-slate-700 group-hover:bg-[#C9A84C] transition-colors inline-block flex-shrink-0" />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-[#C9A84C] font-bold text-[10px] mb-5 uppercase tracking-[0.18em]">
              Products
            </h4>
            <ul className="space-y-3">
              {productLinks.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.path}
                    className="text-slate-400 hover:text-[#C9A84C] text-xs transition-colors cursor-pointer flex items-center gap-2 group"
                  >
                    <span className="w-3 h-px bg-slate-700 group-hover:bg-[#C9A84C] transition-colors inline-block flex-shrink-0" />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-[#C9A84C] font-bold text-[10px] mb-5 uppercase tracking-[0.18em]">
              Contact
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-slate-400 text-xs">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-map-pin-2-line text-[#C9A84C] leading-none" />
                </div>
                <span>
                  One Embankment
                  <br />
                  Leeds LS1 4DW, United Kingdom
                </span>
              </li>
              <li className="flex items-start gap-3 text-slate-400 text-xs">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-phone-line text-[#C9A84C] leading-none" />
                </div>
                <div className="flex flex-col gap-1">
                  <a href="tel:+442030976185" className="hover:text-[#C9A84C] transition-colors">
                    +44 20 3097 6185
                  </a>
                  <a href="tel:+14372922296" className="hover:text-[#C9A84C] transition-colors">
                    +1 437 292 2296
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-3 text-slate-400 text-xs">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-[#C9A84C] leading-none" />
                </div>
                <a href="mailto:invest@thorpedellow.com" className="hover:text-[#C9A84C] transition-colors">
                  invest@thorpedellow.com
                </a>
              </li>
            </ul>

            <div className="flex flex-wrap gap-2.5 mt-6">
              {[
                { icon: "ri-linkedin-fill", href: "#" },
                { icon: "ri-twitter-x-line", href: "#" },
              ].map((s) => (
                <a
                  key={s.icon}
                  href={s.href}
                  rel="nofollow"
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-white/10 text-slate-500 hover:border-[#C9A84C]/50 hover:text-[#C9A84C] transition-all cursor-pointer"
                >
                  <i className={`${s.icon} text-xs leading-none`} />
                </a>
              ))}
              <a
                href="https://portal.thorpedellow.com"
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#C9A84C]/30 text-[#C9A84C] text-xs font-semibold hover:bg-[#C9A84C]/10 transition-all cursor-pointer"
              >
                <i className="ri-user-line text-xs leading-none" />
                Portal
              </a>
              <button
                type="button"
                onClick={() => { const el = document.querySelector("#vapi-widget-floating-button"); if (el instanceof HTMLElement) el.click(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#C9A84C] text-[#071A2E] text-xs font-semibold hover:bg-[#B8953B] transition-all cursor-pointer"
              >
                <i className="ri-calendar-check-line text-xs leading-none" />
                Schedule a Call
              </button>
            </div>
          </div>
        </div>

        {/* Regulatory Disclaimer */}
        <div className="border-t border-white/8 pt-8 mb-6">
          <p className="text-slate-600 text-[11px] leading-relaxed max-w-5xl">
            <strong className="text-slate-400">Regulatory Notice:</strong> Thorpe Property Development Ltd (Company No. 07590563), trading as Thorpe Dellow. Registered in England and Wales. Registered office: One Embankment, Leeds LS1 4DW, United Kingdom. This website and all financial promotions are issued in accordance with Section 21 of the Financial Services and Markets Act 2000 (FSMA). All content is directed solely at Certified High Net Worth Individuals, Self-Certified Sophisticated Investors, or eligible retail investors. Investments are speculative and involve risk. Investors may lose some or all of their capital. Past performance is not indicative of future results. Tax treatment depends on individual circumstances. Thorpe Dellow strongly recommends all investors seek independent financial and tax advice prior to investing. NASDAQ Dubai listed products are subject to additional regulatory requirements and local jurisdiction rules.
          </p>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/6 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-slate-600 text-[11px]">&copy; 2026 Thorpe Dellow. All rights reserved.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/privacy-policy" className="text-slate-600 hover:text-slate-400 text-[11px] transition-colors cursor-pointer whitespace-nowrap">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="text-slate-600 hover:text-slate-400 text-[11px] transition-colors cursor-pointer whitespace-nowrap">
              Terms of Use
            </Link>
            <Link to="/risk-disclaimer" className="text-slate-600 hover:text-slate-400 text-[11px] transition-colors cursor-pointer whitespace-nowrap">
              Risk Disclaimer
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
